import { queryOptions } from '@tanstack/react-query';

import { ONE_DAY } from '@/constants/time';
import type { components } from '@primum/api-types';
import { fetchClient } from '@/util/fetchClient';

export type CatalogPublication = components['schemas']['CatalogPublication'];

export const publicationLookupKeys = {
  url: (url: string) => ['publicationLookup', url] as const,
};

/**
 * The catalog work a link names, or null when the catalog holds none. The
 * server matches it by the same PMID, PMCID, DOI or NCT identity shares use,
 * so a link to a paper Primum already knows resolves to that paper's record
 * and summary.
 *
 * Only a real answer is null. A failure throws, so the query records an error
 * rather than caching a miss for a day: a reader who opened the thread during
 * a blip or a token refresh would otherwise get the browser for every catalog
 * paper until tomorrow. The press that hits an error still opens the page.
 */
async function lookupPublication(url: string): Promise<CatalogPublication | null> {
  const { data, error } = await fetchClient.GET('/v2/publications/lookup', {
    params: { query: { url } },
  });

  if (error || !data?.success) {
    throw new Error('Publication lookup failed');
  }

  return data.data ?? null;
}

/**
 * A match or a miss is kept for a day, since a catalog row is stable. A
 * failure is not retried in the background; the next mount or press asks again.
 */
export function getPublicationLookupQueryOptions(url: string) {
  return queryOptions({
    queryKey: publicationLookupKeys.url(url),
    queryFn: () => lookupPublication(url),
    staleTime: ONE_DAY,
    gcTime: ONE_DAY,
    retry: false,
  });
}

/**
 * Whether a catalog match is worth a sheet rather than the page. A study,
 * trial or guideline is; a plain web page the catalog happens to hold has
 * nothing to show beyond its title until it has a summary.
 */
export function opensAsReference(publication: CatalogPublication | null | undefined): boolean {
  return Boolean(publication && (publication.kind !== 'web' || publication.summary));
}
